import { STRIPE_FREE_FOR_LIFE_PLAN_KEY } from "@nfp-tracker/utils";
import { supabase } from "./client";

export type AccountSex = "male" | "female";
export type AccountRole = "tracking" | "viewing" | "instructor";
export type TrackingGoal = "TTC" | "AP" | "Single" | "Pregnant";

const normalizeRole = (role: string | null | undefined): AccountRole => {
  if (role === "tracking" || role === "instructor") {
    return role;
  }

  return "viewing";
};

const normalizeSex = (account: unknown): AccountSex | null => {
  const candidate = (account as { sex?: string } | null)?.sex;
  if (candidate === "male" || candidate === "female") {
    return candidate;
  }

  return null;
};

const normalizeTrackingGoal = (goal: string | null | undefined): TrackingGoal => {
  if (goal === "AP" || goal === "Single" || goal === "Pregnant") {
    return goal;
  }

  return "TTC";
};

export type OwnerContext = {
  ownerUserId: string;
  actorUserId: string;
  partnerUserId: string | null;
  /** Set only when an instructor is deliberately viewing a client's chart. */
  viewingClientUserId: string | null;
};

type ResolveOwnerContextOptions = {
  /**
   * Client an instructor has explicitly opened. Ignored unless the link is still active —
   * a stale id (revoked link, tampered storage) silently falls back to the actor's own data
   * rather than erroring, so a revoked instructor just lands back on their own account.
   */
  instructorClientUserId?: string | null;
};

export const resolveOwnerContext = async (
  actorUserId: string,
  options: ResolveOwnerContextOptions = {}
): Promise<OwnerContext> => {
  const requestedClientId = options.instructorClientUserId || null;

  if (requestedClientId && requestedClientId !== actorUserId) {
    const { data: canView, error: canViewError } = await supabase.rpc(
      "instructor_can_view_client",
      { p_client_id: requestedClientId }
    );

    // A failed check is not a "no": falling back to the actor's own data here would get cached
    // under the client's URL. Throw so the query retries / surfaces an error instead.
    if (canViewError) {
      throw canViewError;
    }

    if (canView) {
      return {
        ownerUserId: requestedClientId,
        actorUserId,
        partnerUserId: null,
        viewingClientUserId: requestedClientId,
      };
    }
  }

  const { data: spouseLink } = await supabase
    .from("user_data_links")
    .select("owner_user_id")
    .eq("grantee_user_id", actorUserId)
    .eq("link_type", "spouse")
    .eq("is_active", true)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  const ownerUserId = spouseLink?.owner_user_id || actorUserId;

  const { data: partnerLink } = await supabase
    .from("user_data_links")
    .select("grantee_user_id")
    .eq("owner_user_id", ownerUserId)
    .eq("link_type", "spouse")
    .eq("is_active", true)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  return {
    ownerUserId,
    actorUserId,
    partnerUserId: partnerLink?.grantee_user_id || null,
    viewingClientUserId: null,
  };
};

type EnsureBaselineInput = {
  userId: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

export const ensureUserBaselineRows = async ({
  userId,
  email,
  firstName,
  lastName,
}: EnsureBaselineInput): Promise<void> => {
  const normalizedEmail = email?.trim().toLowerCase() || null;
  const normalizedFirstName = firstName?.trim() || null;
  const normalizedLastName = lastName?.trim() || null;

  const { error: accountError } = await supabase.from("user_accounts").upsert(
    {
      user_id: userId,
      email: normalizedEmail,
      first_name: normalizedFirstName,
      last_name: normalizedLastName,
    },
    { onConflict: "user_id", ignoreDuplicates: true }
  );

  if (accountError) {
    throw accountError;
  }

  const { error: subscriptionError } = await supabase
    .from("user_subscriptions")
    .upsert({ owner_user_id: userId }, { onConflict: "owner_user_id", ignoreDuplicates: true });

  if (subscriptionError) {
    throw subscriptionError;
  }
};

/**
 * The account's role, or null when it couldn't be read — callers must not treat a failed lookup
 * as "viewing" (that would route an instructor into the tracking app and persist a wrong hint).
 */
export const loadAccountRole = async (userId: string): Promise<AccountRole | null> => {
  const { data, error } = await supabase
    .from("user_accounts")
    .select("role")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    return null;
  }

  return normalizeRole(data?.role);
};

export const loadAccountProfile = async (userId: string) => {
  const [{ data: account }, { data: tracking }] = await Promise.all([
    supabase.from("user_accounts").select("*").eq("user_id", userId).maybeSingle(),
    supabase.from("user_tracking_profiles").select("*").eq("user_id", userId).maybeSingle(),
  ]);

  return {
    id: userId,
    created_at: account?.created_at || null,
    email: account?.email || null,
    first_name: account?.first_name || null,
    last_name: account?.last_name || null,
    sex: normalizeSex(account),
    role: normalizeRole(account?.role),
    timezone: account?.timezone || "UTC",
    age: tracking?.age || null,
    weight_kg: tracking?.weight_kg || null,
    is_postpartum: tracking?.is_postpartum || false,
    notification_enabled: account?.notification_enabled ?? true,
    notification_time: account?.notification_time || null,
    theme_preference: account?.theme_preference || null,
    last_notification_sent: account?.last_notification_sent || null,
    push_notifications_enabled: account?.push_notifications_enabled ?? false,
    notification_methods: account?.notification_methods || ["email"],
  };
};

export const loadOwnerAccount = async (ownerUserId: string) => {
  const [{ data: account }, { data: tracking }, { data: sub }, { data: partnerLink }] = await Promise.all([
    supabase.from("user_accounts").select("*").eq("user_id", ownerUserId).maybeSingle(),
    supabase.from("user_tracking_profiles").select("goal, methods, method_other, optional_fields").eq("user_id", ownerUserId).maybeSingle(),
    supabase.from("user_subscriptions").select("*").eq("owner_user_id", ownerUserId).maybeSingle(),
    supabase
      .from("user_data_links")
      .select("grantee_user_id")
      .eq("owner_user_id", ownerUserId)
      .eq("link_type", "spouse")
      .eq("is_active", true)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle(),
  ]);

  const freeForLife = sub?.plan_key === STRIPE_FREE_FOR_LIFE_PLAN_KEY;

  return {
    id: ownerUserId,
    primary_user_id: ownerUserId,
    partner_user_id: partnerLink?.grantee_user_id || null,
    first_name: account?.first_name || null,
    last_name: account?.last_name || null,
    role: normalizeRole(account?.role),
    sex: normalizeSex(account),
    goal: normalizeTrackingGoal(tracking?.goal),
    methods: tracking?.methods?.length ? tracking.methods : ["Marquette"],
    method_other: tracking?.method_other || null,
    timezone: account?.timezone || "UTC",
    optional_fields: tracking?.optional_fields || null,
    instructor_name: null,
    instructor_email: null,
    stripe_customer_id: sub?.stripe_customer_id || null,
    stripe_subscription_id: sub?.stripe_subscription_id || null,
    stripe_price_id: sub?.stripe_price_id || null,
    stripe_product_id: sub?.stripe_product_id || null,
    billing_provider: sub?.billing_provider || null,
    app_store_product_id: sub?.app_store_product_id || null,
    app_store_original_transaction_id: sub?.app_store_original_transaction_id || null,
    app_store_transaction_id: sub?.app_store_transaction_id || null,
    app_store_environment: sub?.app_store_environment || null,
    plan_key: sub?.plan_key || null,
    subscription_status: freeForLife ? "active" : (sub?.subscription_status || "trialing"),
    trial_end_date: freeForLife ? null : (sub?.trial_end_date || null),
    subscription_end_date: freeForLife ? null : (sub?.subscription_end_date || null),
    subscription_renewal_date: freeForLife ? null : (sub?.subscription_renewal_date || null),
    subscription_auto_renews: freeForLife ? null : (sub?.subscription_auto_renews ?? null),
    created_at: account?.created_at || null,
  };
};

export const setSpouseLink = async (
  ownerUserId: string,
  partnerUserId: string | null,
  accessLevel: "read" | "write" = "write"
) => {
  if (!partnerUserId) {
    await supabase
      .from("user_data_links")
      .update({ is_active: false, revoked_at: new Date().toISOString() })
      .eq("owner_user_id", ownerUserId)
      .eq("link_type", "spouse")
      .eq("is_active", true);
    return;
  }

  await supabase.from("user_data_links").upsert({
    owner_user_id: ownerUserId,
    grantee_user_id: partnerUserId,
    link_type: "spouse",
    access_level: accessLevel,
    is_active: true,
    created_by_user_id: ownerUserId,
    revoked_at: null,
  });
};

export const needsOnboarding = async (actorUserId: string): Promise<boolean> => {
  const ctx = await resolveOwnerContext(actorUserId);

  // Viewing users linked to an owner do not need tracking onboarding.
  if (ctx.ownerUserId !== actorUserId) {
    return false;
  }

  const { data: account } = await supabase
    .from("user_accounts")
    .select("*")
    .eq("user_id", actorUserId)
    .maybeSingle();

  const accountSex = normalizeSex(account);
  const accountRole = normalizeRole(account?.role);

  if (accountRole === "instructor") {
    // Instructors never run tracking onboarding; their only setup step is a profile row
    // (display name), which `accept_instructor_invite` also requires before linking.
    const { data: instructorProfile } = await supabase
      .from("instructor_profiles")
      .select("instructor_id")
      .eq("user_id", actorUserId)
      .maybeSingle();

    return !instructorProfile;
  }

  if (accountSex !== "male" && accountSex !== "female") {
    return true;
  }

  if (accountSex === "male" || accountRole === "viewing") {
    // Viewing users (typically male) can continue once linked to a tracking owner.
    return !ctx.partnerUserId;
  }

  if (accountRole !== "tracking") {
    return true;
  }

  const { data: tracking, error: trackingError } = await supabase
    .from("user_tracking_profiles")
    .select("age, goal, methods, method_other")
    .eq("user_id", actorUserId)
    .maybeSingle();

  if (trackingError) {
    // Fail open — a fetch error (e.g. schema mismatch during a migration rollout) must
    // never silently bounce an already-onboarded user back into the onboarding wizard.
    console.error("[needsOnboarding] Failed to load tracking profile", trackingError);
    return false;
  }

  if (!tracking?.goal) {
    return true;
  }

  if (!tracking.methods?.length) {
    return true;
  }

  if (tracking.methods.includes("Other") && !tracking.method_other) {
    return true;
  }

  if (tracking.age == null) {
    return true;
  }

  return false;
};
